import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Plane, TriangleAlert } from 'lucide-react'
import { API_ROUTES, LIVE_BET_DELAY_MS, legKey, type Slip, type SlipsMineResponse, type StockbetQuotesResponse } from '@brochure/shared'
import { Flap } from '@/components/home/DepartureBoard'
import { apiFetch } from '@/lib/api'
import { draftFromSlip, nothingPlayable, pendingDurationMs, pendingRemainingMs, resolvedPending } from '@/lib/slip'
import { cn } from '@/lib/utils'

/** Quanto resta visibile "Giocata!" prima di sparire. */
const PLACED_MS = 2500
/** Il conto alla rovescia si rilegge spesso, ma lo stato cambia (e la barra si ridisegna) una volta al secondo. */
const TICK_MS = 250

const cardClass = 'pointer-events-auto mx-auto mt-2 w-full max-w-2xl rounded-2xl border-2 px-4 py-3 font-body shadow-[0_8px_24px_rgb(0_0_0/0.35)]'

export type PendingTiming = { remainingMs: number; totalMs: number }

/**
 * Tempi di una schedina in attesa: durata della sua attesa (`placedAt` → `acceptAt`, così un ritardo diverso dai
 * 5 s di default resta proporzionato) e millisecondi che mancano ad `acceptAt` con l'orologio del server, il cui
 * scarto si misura a ogni lettura di `data` (`serverNow` contro l'ora del client): un telefono avanti o indietro non
 * sposta la barra.
 */
export function usePendingClock(data: SlipsMineResponse | undefined) {
  const fetchedAt = useMemo(() => Date.now(), [data])
  return (slip: Slip): PendingTiming => {
    const totalMs = pendingDurationMs(slip, LIVE_BET_DELAY_MS)
    const remainingMs = data && slip.acceptAt ? pendingRemainingMs(slip.acceptAt, data.serverNow, fetchedAt, Date.now(), totalMs) : 0
    return { remainingMs, totalMs }
  }
}

type GateProgressProps = PendingTiming & {
  /** `remainingMs` e `totalMs` si leggono al montaggio: da lì la barra va da sola fino in fondo. */
  /** Tempo scaduto ma il server non ha ancora risposto: riflesso veloce, indeterminato. */
  isOvertime?: boolean
  /** La versione grande (barra in basso) ha l'aereo sul bordo del sole; quella delle "Schedine" no. */
  withPlane?: boolean
  className?: string
}

/**
 * Pista d'imbarco: la perforazione tratteggiata su cui avanza il riempimento color sole, con un riflesso che scorre.
 * Il riempimento è largo quanto la pista e si sposta con `translateX` (Web Animations, lineare, durata = tempo che
 * manca): solo il compositor lavora, nessun render per fotogramma, e il riflesso dentro non si deforma come farebbe con
 * `scaleX`. L'aereo sta su un secondo strato con la stessa animazione, fuori dalla pista per non essere tagliato.
 */
export function GateProgress({ remainingMs, totalMs, isOvertime = false, withPlane = false, className }: GateProgressProps) {
  const fillRef = useRef<HTMLSpanElement>(null)
  const riderRef = useRef<HTMLSpanElement>(null)
  const [{ start, total }] = useState({ start: remainingMs, total: totalMs })
  const from = `translateX(${(-start / total) * 100}%)`

  useEffect(() => {
    const animations = [fillRef.current, riderRef.current].flatMap((el) =>
      el ? [el.animate([{ transform: from }, { transform: 'translateX(0)' }], { duration: start, easing: 'linear', fill: 'forwards' })] : [],
    )
    return () => animations.forEach((a) => a.cancel())
  }, [from, start])

  return (
    <span aria-hidden="true" className={cn('relative block', withPlane ? 'h-5' : 'h-1.5', className)}>
      <span className={cn('gate-track absolute inset-x-0 top-1/2 block -translate-y-1/2 overflow-hidden rounded-full bg-paper/[0.06]', withPlane ? 'h-2.5' : 'h-1.5')}>
        <span ref={fillRef} style={{ transform: from }} className="absolute inset-0 block overflow-hidden rounded-full bg-sun">
          <span
            className={cn(
              'absolute inset-y-0 left-0 block w-2/5 bg-linear-to-r from-transparent via-paper/70 to-transparent motion-reduce:hidden',
              isOvertime ? 'motion-safe:animate-gate-shimmer' : 'motion-safe:animate-gate-sheen',
            )}
          />
        </span>
      </span>
      {withPlane && (
        // Strato largo come la pista, spostato come il riempimento: l'aereo sta sul suo bordo destro, sul muso del sole
        // (da 0 alla fine della pista; metà aereo sporge ai lati, il padding di chi la contiene gli fa posto).
        <span ref={riderRef} style={{ transform: from }} className="absolute inset-0 block">
          <span className="absolute top-1/2 right-0 grid size-5 translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-ink ring-2 ring-sun">
            <Plane className="size-3 rotate-45 text-sun" strokeWidth={2.5} />
          </span>
        </span>
      )}
    </span>
  )
}

/** Secondi che mancano a `deadline` (ora del client), arrotondati in su; 0 quando è passata. */
function useSecondsLeft(deadline: number) {
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil((deadline - Date.now()) / 1000)))
  useEffect(() => {
    if (left <= 0) return
    // Stesso valore → React non ridisegna: un render al secondo anche se si controlla più spesso.
    const id = window.setInterval(() => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000))), TICK_MS)
    return () => window.clearInterval(id)
  }, [deadline, left])
  return left
}

function useSummary(slip: Slip) {
  const { t } = useTranslation()
  return t('stockbet.pending.summary', { type: t(`stockbet.slip.types.${slip.type}`), count: slip.totalStake })
}

/** Giocata live in attesa: "Conferma della giocata…", il conto alla rovescia sulle palette, la pista d'imbarco. */
function PendingTicket({ slip, remainingMs, totalMs }: { slip: Slip } & PendingTiming) {
  const { t } = useTranslation()
  const summary = useSummary(slip)
  const [deadline] = useState(() => Date.now() + remainingMs)
  const left = useSecondsLeft(deadline)
  const isOvertime = left === 0
  const totalS = Math.ceil(totalMs / 1000)
  return (
    <article data-testid="live-slip-pending" className={cn(cardClass, 'night border-paper shadow-[3px_3px_0_var(--color-sun),0_8px_24px_rgb(0_0_0/0.35)]')}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{isOvertime ? t('stockbet.pending.almost') : t('stockbet.pending.confirming')}</p>
          <p className="truncate text-xs text-paper/60">{summary}</p>
        </div>
        {!isOvertime && (
          <span aria-hidden="true" className="flex shrink-0 items-end gap-[0.06em] text-2xl leading-none font-semibold tabular-nums">
            {String(left).padStart(2, '0').split('').map((char, i) => <Flap key={i} char={char} />)}
            <span className="ml-1 text-xs text-paper/60">s</span>
          </span>
        )}
      </div>
      <div
        role="progressbar"
        aria-label={t('stockbet.pending.progress')}
        aria-valuemin={0}
        aria-valuemax={totalS}
        aria-valuenow={totalS - Math.min(left, totalS)}
        aria-valuetext={isOvertime ? t('stockbet.pending.almost') : t('stockbet.pending.secondsLeft', { count: left })}
        className="mt-2.5 px-2.5"
      >
        <GateProgress remainingMs={remainingMs} totalMs={totalMs} isOvertime={isOvertime} withPlane />
      </div>
    </article>
  )
}

/** Giocata confermata: il biglietto diventa color sole, l'aereo decolla, poi sparisce da solo. */
function PlacedTicket({ slip, onDone }: { slip: Slip; onDone: (id: string) => void }) {
  const { t } = useTranslation()
  const summary = useSummary(slip)
  useEffect(() => {
    const id = window.setTimeout(() => onDone(slip.id), PLACED_MS)
    return () => window.clearTimeout(id)
  }, [slip.id, onDone])
  return (
    <article data-testid="live-slip-placed" className={cn(cardClass, 'flex items-center gap-3 border-ink bg-sun text-ink motion-safe:animate-reveal')}>
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-ink text-sun">
        <Check className="size-5" strokeWidth={3} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-2xl leading-tight font-medium italic">{t('stockbet.pending.placed')}</p>
        <p className="truncate text-xs font-semibold text-ink/70">{summary}</p>
      </div>
      <Plane aria-hidden="true" className="size-6 shrink-0 rotate-45 motion-safe:animate-takeoff" />
    </article>
  )
}

/** Giocata annullata alla conferma: perché, e le due strade (rigiocare con le quote nuove o chiudere). */
function RejectedTicket({ slip, onReplay, onClose }: { slip: Slip; onReplay: () => void; onClose: () => void }) {
  const { t } = useTranslation()
  const summary = useSummary(slip)
  // Esito chiuso: si leggono una volta le quote delle sue partite. Se non resta niente in offerta (partita al
  // pareggio o conclusa) "Rigioca" sparisce; altrimenti diventa "Rivedi la schedina" (si toglie il chiuso e si gioca il resto).
  const isClosed = slip.rejectReason === 'marketClosed'
  const [quotes, setQuotes] = useState<StockbetQuotesResponse>()
  useEffect(() => {
    if (!isClosed) return
    // Gli eventi rigiocabili (partite e antepost) per chiave: se non ne resta nessuno, non c'è niente in offerta.
    const keys = draftFromSlip(slip).legs.map(legKey)
    if (keys.length === 0) return setQuotes({})
    const controller = new AbortController()
    apiFetch<StockbetQuotesResponse>(`${API_ROUTES.tournaments}/${slip.tournamentId}/stockbet/quotes?matches=${keys.join(',')}`, { signal: controller.signal })
      .then(setQuotes)
      .catch(() => {}) // senza quote resta "Rivedi la schedina": lo sheet segnalerà gli esiti chiusi
    return () => controller.abort()
  }, [isClosed, slip])
  const canReplay = !isClosed || !nothingPlayable(slip, quotes)
  return (
    <article data-testid="live-slip-rejected" className={cn(cardClass, 'night border-coral shadow-[3px_3px_0_var(--color-coral),0_8px_24px_rgb(0_0_0/0.35)] motion-safe:animate-reveal')}>
      <div className="flex items-start gap-3">
        <TriangleAlert className="mt-0.5 size-5 shrink-0 text-coral" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t(`stockbet.pending.rejected.${slip.rejectReason ?? 'oddsChanged'}`)}</p>
          <p className="truncate text-xs text-paper/60">{summary}</p>
        </div>
      </div>
      {/* Affiancati se ci stanno, uno sotto l'altro sui telefoni stretti: ognuno si allarga sulla sua riga. */}
      <div className="mt-3 flex flex-wrap gap-2 *:grow">
        {canReplay && (
          <button type="button" data-testid="live-slip-replay" onClick={onReplay} className="min-h-11 rounded-xl bg-sun px-4 text-sm font-semibold text-ink transition-transform duration-150 active:scale-[0.98]">
            {t(isClosed ? 'stockbet.pending.review' : 'stockbet.pending.replay')}
          </button>
        )}
        <button type="button" data-testid="live-slip-close" onClick={onClose} className="min-h-11 rounded-xl border border-paper/30 px-4 text-sm font-semibold text-paper transition-colors hover:bg-paper/10">
          {t('stockbet.pending.close')}
        </button>
      </div>
    </article>
  )
}

type LiveSlipsProps = {
  data: SlipsMineResponse | undefined
  /** "Rigioca con le nuove quote": la pagina rimette la schedina in bozza e apre lo sheet. */
  onReplay: (slip: Slip) => void
}

/**
 * Le giocate live dell'ospite, nella fascia in basso sopra la `SlipBar`: una barra d'imbarco per ogni schedina
 * `pending` e, quando il server risponde (ricarica di `data` via WebSocket), "Giocata!" o l'annullamento.
 * Tutto viene da `data`: la barra sopravvive a cambi di pagina e ricariche. L'esito si riconosce confrontando
 * le schedine in attesa viste alla lettura precedente con quelle di adesso.
 */
export function LiveSlips({ data, onReplay }: LiveSlipsProps) {
  const seen = useRef<ReadonlySet<string> | null>(null)
  const [results, setResults] = useState<Slip[]>([])
  const timing = usePendingClock(data)

  useEffect(() => {
    if (!data) return
    const previous = seen.current
    seen.current = new Set(data.slips.filter((s) => s.status === 'pending').map((s) => s.id))
    // Alla prima lettura non c'è un "prima": le schedine già decise si vedono solo nelle "Schedine".
    const resolved = previous ? resolvedPending(previous, data.slips) : []
    if (resolved.length > 0) setResults((current) => [...current.filter((r) => !resolved.some((s) => s.id === r.id)), ...resolved])
  }, [data])

  const dismiss = useCallback((id: string) => setResults((current) => current.filter((r) => r.id !== id)), [])
  const pending = data?.slips.filter((s) => s.status === 'pending') ?? []

  return (
    <>
      {/* Regione sempre presente (anche vuota): gli screen reader annunciano l'esito quando compare. */}
      <div role="status" aria-live="polite" data-testid="live-slip-results">
        {results.map((slip) =>
          slip.status === 'rejected' ? (
            <RejectedTicket key={slip.id} slip={slip} onClose={() => dismiss(slip.id)} onReplay={() => { dismiss(slip.id); onReplay(slip) }} />
          ) : (
            <PlacedTicket key={slip.id} slip={slip} onDone={dismiss} />
          ),
        )}
      </div>
      {pending.map((slip) => <PendingTicket key={slip.id} slip={slip} {...timing(slip)} />)}
    </>
  )
}
